import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import { useAuth } from '../../contexts/AuthContext';
import { SERVER_BASE_URL } from '../../utils/api';

const BONUS_TYPE_LABELS = {
  linear: 'Линейный',
  activity_compression: 'Компрессия',
  mentor: 'Наставник',
  leader: 'Лидерский',
  company: 'Компания',
  promo: 'Промо'
};

const Wrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 32px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 24px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  padding: 12px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 4px 0;
`;

const StatValue = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;
const ControlsSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: ${props => props.$active ? '#3b82f6' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f9fafb'};
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 24px 24px 0 24px;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
`;

const CardContent = styled.div`
  padding: 0 24px 24px 24px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  padding: 12px 16px;
  font-size: 0.875rem;
  text-align: ${props => props.textalign || 'left'};
  border-bottom: 1px solid #e5e7eb;
`;

const Td = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
  text-align: ${props => props.textalign || 'left'};
  
  &:hover {
    background: #f9fafb;
  }
`;

const ChartContainer = styled.div`
  height: 320px;
  width: 100%;
`;

const SelectWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Select = styled.select`
  appearance: none;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 32px 8px 12px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  min-width: 160px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  
  &::after {
    content: '';
    display: block;
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid #6b7280;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 16px;
  padding: 16px;
  
  @media (max-width: 768px) {
    gap: 24px;
    flex-wrap: wrap;
  }
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendLine = styled.div`
  width: 20px;
  height: 3px;
  background: ${props => props.color};
  border-radius: 2px;
`;

const LegendDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${props => props.color};
  border-radius: 50%;
  margin-left: -4px;
`;

const LegendText = styled.span`
  font-size: 0.875rem;
  color: #374151;
  font-weight: 500;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
`;

const ErrorText = styled.div`
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 1.1rem;
`;

const EmptyText = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1rem;
  background: #f8f9fa;
  border-radius: 8px;
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 0.85rem;
  color: white;
  background: ${props => {
    switch(props.level) {
      case 1: return '#28a745';
      case 2: return '#ffc107';
      case 3: return '#17a2b8';
      default: return '#6c757d';
    }
  }};
`;

const PointsAmount = styled.span`
  color: #28a745;
  font-weight: 600;
  font-size: 1.1rem;
`;

const OrderAmount = styled.span`
  color: #007bff;
  font-weight: 500;
`;

// Кастомный Tooltip для графика
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 12, color: '#444', fontSize: 14 }}>
        <div style={{ fontWeight: '600', marginBottom: 8 }}>{label}</div>
        {payload.map((entry, index) => (
          <div key={index} style={{ color: entry.color, marginBottom: 4 }}>
            {entry.name}: {entry.value}
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const ReferralProgram = () => {
  const { isAuthenticated, isSport } = useAuth();
  const [pointsHistory, setPointsHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [view, setView] = useState("chart");
  const [period, setPeriod] = useState("7d");
  
  // State for referral journal data
  const [referralStats, setReferralStats] = useState({ registrations: 0, purchases: 0 });
  const [referralRows, setReferralRows] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [referralLoading, setReferralLoading] = useState(true);
  const [referralError, setReferralError] = useState(null);
  
  // Определяем статус пользователя
  const maxAllowedLevel = 6;

  // Загружаем данные журнала рефералов
  useEffect(() => {
    const loadReferralData = async () => {
      if (!isAuthenticated()) return;
      
      try {
        setReferralLoading(true);
        setReferralError(null);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${SERVER_BASE_URL}/api/referrals/my-users`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.success && data.data) {
            const users = data.data;
            
            // Count total registrations (excluding users without referrer - they are direct)
            const totalRegistrations = users.filter(u => u.referrerId !== null).length;
            
            // Count purchases - users that have lastPurchaseDate
            const totalPurchases = users.filter(u => u.lastPurchaseDate !== null).length;
            
            setReferralStats({
              registrations: totalRegistrations,
              purchases: totalPurchases
            });
            
            // Group registrations by date for the table
            const registrationsByDate = {};
            
            // Determine date range based on period
            const now = new Date();
            let daysToShow = 7;
            if (period === '30d') daysToShow = 30;
            else if (period === 'all') daysToShow = 365; // Show last year for "all"
            
            // Initialize dates
            for (let i = daysToShow - 1; i >= 0; i--) {
              const date = new Date(now);
              date.setDate(date.getDate() - i);
              const dateKey = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
              registrationsByDate[dateKey] = { registrations: 0 };
            }
            
            // Count registrations per date
            users.forEach(user => {
              if (user.registrationDate && user.referrerId !== null) {
                const regDate = new Date(user.registrationDate);
                const timeDiff = now - regDate;
                const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
                
                if (daysDiff <= daysToShow) {
                  const dateKey = regDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
                  if (registrationsByDate[dateKey]) {
                    registrationsByDate[dateKey].registrations++;
                  }
                }
              }
            });
            
            // Convert to array for table and chart
            const rows = Object.entries(registrationsByDate).map(([date, data], index) => ({
              id: index + 1,
              date,
              registrations: data.registrations
            }));
            
            setReferralRows(rows);
            setChartData(rows.map(row => ({
              date: row.date,
              registrations: row.registrations
            })));
          }
        } else {
          const errorData = await response.json();
          setReferralError(errorData.message || 'Ошибка загрузки данных журнала рефералов');
        }
      } catch (err) {
        console.error('Error loading referral data:', err);
        setReferralError('Ошибка сети при загрузке данных рефералов');
      } finally {
        setReferralLoading(false);
      }
    };

    loadReferralData();
  }, [isAuthenticated, period]);

  // Загружаем историю реферальных баллов
  useEffect(() => {
    const loadPointsHistory = async () => {
      if (!isAuthenticated()) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${SERVER_BASE_URL}/api/referrals/points-history?page=${pagination.page}&limit=${pagination.limit}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPointsHistory(data.history);
          setPagination(data.pagination);
        } else {
          setError('Ошибка загрузки истории баллов');
        }
      } catch (err) {
        console.error('Error loading points history:', err);
        setError('Ошибка сети при загрузке истории баллов');
      } finally {
        setLoading(false);
      }
    };

    loadPointsHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination.page intent; pagination.limit stable
  }, [isAuthenticated, pagination.page]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <>
      <DashboardHeader />
      <Wrapper>
        <Title>Отчёт по партнёрской программе Lagom Life</Title>
        <p style={{ color: '#666', marginBottom: '24px', fontSize: '14px', lineHeight: 1.6 }}>
          Линейные вознаграждения сейчас: <strong>5% с 1-го уровня</strong>.
          Начисление сразу после оплаты заказа приглашённого.{' '}
          <a href="/referral" style={{ color: '#ff6b35' }}>Подробнее о маркетинге</a>
        </p>
        
        <StatsGrid>
          <StatCard>
            <StatIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#10b981' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </StatIcon>
            <StatContent>
              <StatLabel>Всего регистраций</StatLabel>
              <StatValue>{referralLoading ? '...' : referralStats.registrations}</StatValue>
            </StatContent>
          </StatCard>
          
          <StatCard>
            <StatIcon>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: '#8b5cf6' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </StatIcon>
            <StatContent>
              <StatLabel>Совершили покупки</StatLabel>
              <StatValue>{referralLoading ? '...' : referralStats.purchases}</StatValue>
            </StatContent>
          </StatCard>
        </StatsGrid>

        <ControlsSection>
          <SelectWrapper>
            <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="7d">Последние 7 дней</option>
              <option value="30d">Последние 30 дней</option>
              <option value="all">Всё время</option>
            </Select>
            <SelectArrow />
          </SelectWrapper>
          <ButtonGroup>
            <ToggleButton $active={view === "chart"} onClick={() => setView("chart")}>
              График
            </ToggleButton>
            <ToggleButton $active={view === "table"} onClick={() => setView("table")}>
              Таблица
            </ToggleButton>
          </ButtonGroup>
        </ControlsSection>

        {referralLoading ? (
          <Card>
            <CardContent>
              <LoadingText>Загрузка данных журнала рефералов...</LoadingText>
            </CardContent>
          </Card>
        ) : referralError ? (
          <Card>
            <CardContent>
              <ErrorText>{referralError}</ErrorText>
            </CardContent>
          </Card>
        ) : view === "table" ? (
          <Card>
            <CardHeader>
              <CardTitle>История регистраций по дням</CardTitle>
            </CardHeader>
            <CardContent>
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <Th textalign="left">№</Th>
                      <Th textalign="left">Дата</Th>
                      <Th textalign="right">Регистраций</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {referralRows.length === 0 ? (
                      <tr>
                        <Td colSpan="3" textalign="center">
                          <EmptyText>Нет данных о регистрациях за выбранный период</EmptyText>
                        </Td>
                      </tr>
                    ) : (
                      referralRows.map(row => (
                        <tr key={row.id}>
                          <Td textalign="left">{row.id}</Td>
                          <Td textalign="left">{row.date}</Td>
                          <Td textalign="right">{row.registrations}</Td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableWrapper>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Динамика регистраций</CardTitle>
            </CardHeader>
            <CardContent>
              <ChartContainer>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                    <YAxis allowDecimals={false} stroke="#6b7280" fontSize={12} />
                    <Tooltip 
                      content={<CustomTooltip />}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="registrations" 
                      name="Регистрации"
                      stroke="#22c55e" 
                      strokeWidth={3} 
                      dot={{ r: 5, fill: '#22c55e' }} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
              <LegendContainer>
                <LegendItem>
                  <LegendLine color="#22c55e" />
                  <LegendDot color="#22c55e" />
                  <LegendText>Регистрации</LegendText>
                </LegendItem>
              </LegendContainer>
            </CardContent>
          </Card>
        )}

        {/* История реферальных баллов */}
        <Card style={{ marginTop: '32px' }}>
          <CardHeader>
            <CardTitle>История начисления реферальных баллов</CardTitle>
          </CardHeader>
          <CardContent>
            <TableWrapper>
              <Table>
                <thead>
                  <tr>
                    <Th textalign="left">Реферал</Th>
                    <Th textalign="center">Уровень</Th>
                    <Th textalign="center">Тип бонуса</Th>
                    <Th textalign="center">Время начисления</Th>
                    <Th textalign="right">Сумма покупки</Th>
                    <Th textalign="right">Начислено баллов</Th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <Td colSpan="6" textalign="center">
                        <LoadingText>Загрузка истории баллов...</LoadingText>
                      </Td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <Td colSpan="6" textalign="center">
                        <ErrorText>{error}</ErrorText>
                      </Td>
                    </tr>
                  ) : pointsHistory.length === 0 ? (
                    <tr>
                      <Td colSpan="6" textalign="center">
                        <EmptyText>История реферальных баллов пуста</EmptyText>
                      </Td>
                    </tr>
                  ) : (
                    pointsHistory
                      .filter(item => item.buyer_level <= maxAllowedLevel) // Фильтруем по максимальному уровню
                      .map((item, index) => (
                      <tr key={item.id}>
                        <Td textalign="left">
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                              {item.buyer_first_name} {item.buyer_last_name}
                            </div>
                            <div style={{ fontSize: '0.9rem', color: '#666' }}>
                              Заказ #{item.order_number}
                            </div>
                          </div>
                        </Td>
                        <Td textalign="center">
                          <LevelBadge level={item.buyer_level}>
                            Уровень {item.buyer_level}
                          </LevelBadge>
                        </Td>
                        <Td textalign="center">
                          {BONUS_TYPE_LABELS[item.bonus_type] || item.bonus_type || 'Линейный'}
                        </Td>
                        <Td textalign="center">
                          {formatDate(item.created_at)}
                        </Td>
                        <Td textalign="right">
                          <OrderAmount>
                            {formatCurrency(item.order_amount)}
                          </OrderAmount>
                        </Td>
                        <Td textalign="right">
                          <PointsAmount>
                            +{item.points_awarded} баллов
                          </PointsAmount>
                        </Td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableWrapper>
          </CardContent>
        </Card>
      </Wrapper>
      <DashboardFooter />
    </>
  );
};

export default ReferralProgram; 